import {
    applyDiscountToPrice,
    calculateDiscountPercent,
    calculatePricePerDocument,
    calculateUpgradePrices,
    extractPricePoints,
    extractPricePointsWithoutDiscount,
    extractUpgradePricePoints,
} from '../util/priceCalculation'

export const ONBOARDING_PAY_PENDING_KEY = 'okidoki_onboarding_pay_pending'
export const ONBOARDING_PAY_DISMISSED_KEY = 'okidoki_onboarding_pay_dismissed'

export type OnboardingPayTrigger = 'demo' | 'own' | 'skip'

export const ONBOARDING_PAY_OFFER_CONFIG = {
    after_demo: true,
    after_own: true,
    after_skip: true,
    skip_delay_minutes: 24 * 60,
    bonus_percent: 10,
    max_quantity: 300,
}

const VOLUME_TO_QTY: Record<string, number> = {
    'до 10': 10,
    '11–50': 50,
    '11-50': 50,
    '51–100': 100,
    '51-100': 100,
    '101–300': 300,
    '101-300': 300,
    '300+': 300,
}

export type OnboardingPayOffer = {
    kind: 'standard' | 'custom'
    quantity: number
    bonusDocuments: number
    bonusPercent: number
    priceKop: number
    originalPriceKop: number
    discount: number
    tariffId: string
    name: string
    plannedLabel: string | null
    cartItem: Record<string, unknown>
}

type PendingPayload = {
    trigger: OnboardingPayTrigger
    at: number
}

export function targetQuantityForVolume(
    planned?: string | null,
    maxQuantity = ONBOARDING_PAY_OFFER_CONFIG.max_quantity,
): number | null {
    const raw = (planned || '').trim()
    if (!raw) {
        return null
    }
    const qty = VOLUME_TO_QTY[raw]
    if (!qty) {
        return null
    }
    return Math.min(qty, maxQuantity)
}

export function clampBonusPercent(raw: unknown, fallback = ONBOARDING_PAY_OFFER_CONFIG.bonus_percent): number {
    const n = Number(raw)
    if (!Number.isFinite(n)) {
        return fallback
    }
    return Math.max(0, Math.min(100, Math.round(n)))
}

export function bonusDocumentsFor(quantity: number, bonusPercent: number): number {
    if (quantity <= 0 || bonusPercent <= 0) {
        return 0
    }
    return Math.ceil(quantity * bonusPercent / 100)
}

export function packageV2Tariffs(tariffs: any[]): any[] {
    return (tariffs || [])
        .filter((tariff) => tariff && Number(tariff.prepaid_documents_count) > 0)
        .sort((a, b) => a.prepaid_documents_count - b.prepaid_documents_count)
}

function minPositiveDiscount(tariffs: any[]): number {
    const discounts = (tariffs || [])
        .map((tariff) => tariff.discount || 0)
        .filter((discount: number) => discount > 0)
    if (discounts.length === 0) {
        return 0
    }
    return Math.min(...discounts)
}

export function buildCustomPackageCartItem(tariffs: any[], quantity: number): Record<string, unknown> {
    const sorted = packageV2Tariffs(tariffs)
    const baseTariff = sorted[0] || {}
    const pricePointsWithoutDiscount = extractPricePointsWithoutDiscount(sorted)
    const pricePointsWithDiscount = extractPricePoints(sorted)
    const upgradePricePoints = extractUpgradePricePoints(sorted)
    const pricePerDocumentOriginal = calculatePricePerDocument(quantity, pricePointsWithoutDiscount)
    const pricePerDocumentDiscounted = calculatePricePerDocument(quantity, pricePointsWithDiscount)
    const originalPrice = Math.round(pricePerDocumentOriginal * quantity * 100)
    const discountedPrice = Math.round(pricePerDocumentDiscounted * quantity * 100)
    const calculatedDiscount = calculateDiscountPercent(originalPrice, discountedPrice)
    const upgradePrices = calculateUpgradePrices(quantity, upgradePricePoints)
    const upgradeAdditionalPrice: { [key: string]: number } = {}
    Object.entries(upgradePrices).forEach(([upgradeId, upgradeData]) => {
        upgradeAdditionalPrice[upgradeId] = Math.round(upgradeData.price * quantity * 100)
    })
    return {
        ...baseTariff,
        _id: 'custom',
        name: 'На выбор',
        type: 'package_v2',
        prepaid_documents_count: quantity,
        price: originalPrice,
        discount: calculatedDiscount || minPositiveDiscount(sorted),
        upgrade_additional_price: Object.keys(upgradeAdditionalPrice).length
            ? upgradeAdditionalPrice
            : baseTariff.upgrade_additional_price,
        isMinDiscount: false,
    }
}

export function pickOnboardingPayOffer(
    planned: string | null | undefined,
    tariffs: any[],
    config = ONBOARDING_PAY_OFFER_CONFIG,
): OnboardingPayOffer | null {
    const sorted = packageV2Tariffs(tariffs)
    if (sorted.length === 0) {
        return null
    }
    const plannedLabel = (planned || '').trim() || null
    const mapped = targetQuantityForVolume(plannedLabel, config.max_quantity)
    const bonusPercent = clampBonusPercent(config.bonus_percent)

    if (mapped == null) {
        const cheapest = sorted[0]
        const originalPriceKop = cheapest.price
        const priceKop = applyDiscountToPrice(originalPriceKop, cheapest.discount || 0)
        const quantity = cheapest.prepaid_documents_count
        return {
            kind: 'standard',
            quantity,
            bonusDocuments: bonusDocumentsFor(quantity, bonusPercent),
            bonusPercent,
            priceKop,
            originalPriceKop,
            discount: cheapest.discount || 0,
            tariffId: String(cheapest._id),
            name: cheapest.name || 'Старт',
            plannedLabel: null,
            cartItem: { ...cheapest, type: 'package_v2', discount: cheapest.discount || 0 },
        }
    }

    const quantity = mapped
    const exact = sorted.find((tariff) => tariff.prepaid_documents_count === quantity)
    if (exact) {
        const originalPriceKop = exact.price
        const priceKop = applyDiscountToPrice(originalPriceKop, exact.discount || 0)
        return {
            kind: 'standard',
            quantity,
            bonusDocuments: bonusDocumentsFor(quantity, bonusPercent),
            bonusPercent,
            priceKop,
            originalPriceKop,
            discount: exact.discount || 0,
            tariffId: String(exact._id),
            name: exact.name || 'Пакет',
            plannedLabel,
            cartItem: { ...exact, type: 'package_v2', discount: exact.discount || 0 },
        }
    }

    const cartItem = buildCustomPackageCartItem(sorted, quantity)
    const originalPriceKop = Number(cartItem.price) || 0
    const discount = Number(cartItem.discount) || 0
    return {
        kind: 'custom',
        quantity,
        bonusDocuments: bonusDocumentsFor(quantity, bonusPercent),
        bonusPercent,
        priceKop: applyDiscountToPrice(originalPriceKop, discount),
        originalPriceKop,
        discount,
        tariffId: 'custom',
        name: 'Ваш пакет',
        plannedLabel,
        cartItem,
    }
}

export function onboardingPayOfferMetrikaParams(
    offer: OnboardingPayOffer,
    trigger?: OnboardingPayTrigger | null,
): Record<string, string | number> {
    return {
        step: trigger || 'own',
        kind: offer.kind,
        tariff_id: offer.tariffId,
        tariff_name: offer.name,
        quantity: offer.quantity,
        bonus_documents: offer.bonusDocuments,
        bonus_percent: offer.bonusPercent,
        price_rub: Math.round(offer.priceKop / 100),
    }
}

export function onboardingPayOfferNavigateParams(offer: OnboardingPayOffer): Record<string, string> {
    if (offer.kind === 'custom') {
        return {
            add_to_cart: 'package_v2',
            custom_quantity: String(offer.quantity),
        }
    }
    return {
        add_to_cart: 'package_v2',
        add_to_cart_tariff_id: offer.tariffId,
    }
}

export function formatPayOfferPrice(priceKop: number): string {
    return `${Math.round(priceKop / 100).toLocaleString('ru-RU')} ₽`
}

function readJson(storage: Storage | undefined, key: string): any {
    try {
        const raw = storage?.getItem(key)
        return raw ? JSON.parse(raw) : null
    } catch {
        return null
    }
}

export function readOnboardingPayPending(): PendingPayload | null {
    const data = readJson(globalThis.sessionStorage, ONBOARDING_PAY_PENDING_KEY)
    if (!data || (data.trigger !== 'demo' && data.trigger !== 'own' && data.trigger !== 'skip')) {
        return null
    }
    return { trigger: data.trigger, at: Number(data.at) || 0 }
}

export function isOnboardingPayDismissed(): boolean {
    try {
        return globalThis.localStorage?.getItem(ONBOARDING_PAY_DISMISSED_KEY) === '1'
    } catch {
        return false
    }
}

export function dismissOnboardingPayOffer(): void {
    try {
        globalThis.localStorage?.setItem(ONBOARDING_PAY_DISMISSED_KEY, '1')
        globalThis.sessionStorage?.removeItem(ONBOARDING_PAY_PENDING_KEY)
    } catch {
        // ignore
    }
}

export function queueOnboardingPayOffer(
    trigger: OnboardingPayTrigger,
    config = ONBOARDING_PAY_OFFER_CONFIG,
): void {
    if (isOnboardingPayDismissed()) {
        return
    }
    if (trigger === 'demo' && !config.after_demo) {
        return
    }
    if (trigger === 'own' && !config.after_own) {
        return
    }
    if (trigger === 'skip' && !config.after_skip) {
        return
    }
    const existing = readOnboardingPayPending()
    if (existing) {
        if (existing.trigger === 'skip' && (trigger === 'demo' || trigger === 'own')) {
            try {
                globalThis.sessionStorage?.setItem(
                    ONBOARDING_PAY_PENDING_KEY,
                    JSON.stringify({ trigger, at: Date.now() }),
                )
            } catch {
                // ignore
            }
        }
        return
    }
    try {
        globalThis.sessionStorage?.setItem(
            ONBOARDING_PAY_PENDING_KEY,
            JSON.stringify({ trigger, at: Date.now() }),
        )
    } catch {
        // ignore
    }
}

export function skipDelayMs(config = ONBOARDING_PAY_OFFER_CONFIG): number {
    return Math.max(0, config.skip_delay_minutes) * 60 * 1000
}

export function shouldShowOnboardingPayOffer(opts: {
    experiments?: unknown
    isOnboardingAudience: boolean
    asClient?: boolean
    routeName?: string
    pending?: PendingPayload | null
    now?: number
    config?: typeof ONBOARDING_PAY_OFFER_CONFIG
}): boolean {
    const config = opts.config || ONBOARDING_PAY_OFFER_CONFIG
    if (!opts.isOnboardingAudience) {
        return false
    }
    if (isOnboardingPayDismissed()) {
        return false
    }
    if (opts.asClient) {
        return false
    }
    if (opts.routeName === 'packages-v2' || opts.routeName === 'tariffs') {
        return false
    }
    const pending = opts.pending ?? readOnboardingPayPending()
    if (!pending) {
        return false
    }
    if (pending.trigger === 'skip') {
        const wait = skipDelayMs(config) - ((opts.now ?? Date.now()) - pending.at)
        if (wait > 0) {
            return false
        }
    }
    return true
}
